import clsx from "clsx"
import type { ReactNode } from "react"

import { Button } from "../Button"
import { Icon, type IconName } from "../Icon"
import { IconButton } from "../IconButton"
import type { IconButtonProps } from "../IconButton/IconButton"
import { Text } from "../Text"
import styles from "./Alert.module.css"

export type Colors = "error" | "warn" | "info" | "default"

export type AlertProps = {
  icon?: IconName
  title?: string
  /**
   * Indicates if the title is in a loading state. When true, an animated ellipsis is displayed next to the title.
   */
  titleLoading?: boolean
  subtitle?: ReactNode
  rightButton?: RightButton
  color?: Colors
  footerButtons?: FooterButtons
  noShadow?: boolean
  align?: "center"
}

type RightButton = { onClick: () => void } & (
  | {
      type: "close"
    }
  | { type: "text"; title: string }
  | { type: "text-dimmed"; title: string }
)

export interface FooterButtons {
  /** Dismiss button in the footer, displayed first */
  dismiss?: {
    text: string
    onClick?: () => void
  }
  /** Main action button in the footer, displayed second */
  action?: {
    text: string
    onClick: () => void
    /** Icon displayed on the right of the button, defaults to arrowForward */
    icon?: IconName
  }
}

export function Alert(props: AlertProps) {
  const {
    icon,
    title,
    titleLoading,
    subtitle,
    footerButtons,
    rightButton,
    color = "default",
    noShadow,
    align,
  } = props

  const {
    titleColor,
    textColor,
    closeIconColor,
    dismissButtonColor,
    actionButtonColor,
  } = getAlertColors(color)

  const shouldShowTitle = Boolean(title) || titleLoading
  return (
    <div
      className={clsx(
        styles.root,
        !noShadow && styles.rootShadow,
        styles[`container-color-${color}`],
        align && styles[`align-${align}`],
      )}
    >
      <div className={styles.container}>
        {icon && (
          <div className={styles.alertIcon}>
            <Icon
              name={icon}
              color={textColor}
              size="large"
              className={styles.alertIcon}
            />
          </div>
        )}
        <div className={styles.content}>
          {shouldShowTitle && (
            <Text variant="bold" color={titleColor} noWrap>
              {title}
              {titleLoading && (
                <>
                  <span className={styles.loadingDot}>.</span>
                  <span className={styles.loadingDot}>.</span>
                  <span className={styles.loadingDot}>.</span>
                </>
              )}
            </Text>
          )}
          {Boolean(subtitle) && (
            <Text variant="normal" color={textColor}>
              {subtitle}
            </Text>
          )}
          {(footerButtons?.dismiss || footerButtons?.action) && (
            <div className={styles.footerButtons}>
              {footerButtons.dismiss && (
                <Button
                  onClick={footerButtons.dismiss.onClick}
                  color={dismissButtonColor}
                  invisible
                >
                  {footerButtons.dismiss.text}
                </Button>
              )}
              {footerButtons.action && (
                <Button
                  invisible
                  endIcon={footerButtons.action.icon ?? "arrowForward"}
                  onClick={footerButtons.action.onClick}
                  color={actionButtonColor}
                >
                  {footerButtons.action.text}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {rightButton && <RightButton {...rightButton} color={closeIconColor} />}
    </div>
  )
}

type RightButtonProps = RightButton & {
  color: IconButtonProps["color"]
}

function RightButton(props: RightButtonProps) {
  switch (props.type) {
    case "close":
      return (
        <IconButton
          icon="close"
          onClick={props.onClick}
          color={props.color}
          size="large"
          hasPadding={false}
          hiddenLabel="Close alert"
        />
      )
    case "text":
      return (
        <Button invisible onClick={props.onClick} color={props.color}>
          {props.title}
        </Button>
      )
    case "text-dimmed":
      return (
        <Button
          color={props.color === "tomato" ? "tomato-dimmed" : props.color}
          size="large"
          onClick={props.onClick}
        >
          {props.title}
        </Button>
      )
  }
}

function getAlertColors(color: Colors) {
  switch (color) {
    case "error":
      return {
        titleColor: "tomato11",
        textColor: "tomato11",
        closeIconColor: "tomato",
        dismissButtonColor: "tomato",
        actionButtonColor: "tomato",
      } as const
    case "warn":
      return {
        titleColor: "amber11",
        textColor: "amber11",
        closeIconColor: "amber",
        dismissButtonColor: "amber",
        actionButtonColor: "amber",
      } as const
    case "default":
      return {
        titleColor: "grey12",
        textColor: "grey11",
        closeIconColor: "grey",
        dismissButtonColor: "dark",
        actionButtonColor: "purple",
      } as const
    case "info":
      return {
        titleColor: "purple11",
        textColor: "purple11",
        closeIconColor: "purple",
        dismissButtonColor: "purple",
        actionButtonColor: "purple",
      } as const
  }
}
